import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import { COMPANY_DETAILS } from "../pages/appsettings";

// Theme colors (matches app brand green #2d5a3d)
const THEME_GREEN = [45, 90, 61];

const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = "/mainlogo.png";
  });

/**
 * Builds a branded, landscape A4 Inventory Items Report PDF using
 * jsPDF + autoTable.
 *
 * @param {Object} options
 * @param {Array}  options.items         - Item rows from the report API
 * @param {boolean} options.includeFilters - Whether to print filter summary
 * @param {Object}  options.filters      - { search, category, status } labels
 * @returns {Promise<jsPDF>} The generated jsPDF document (not yet saved)
 */
export const buildItemsReportPDF = async ({ items, includeFilters = false, filters = {} }) => {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  // ----- Letterhead: logo + company details -----
  const logo = await loadLogo();
  const logoX = margin;
  const logoY = 13;
  const logoSize = 20;

  if (logo) {
    doc.addImage(logo, "PNG", logoX, logoY, logoSize, logoSize);
  } else {
    // Fallback branded box
    doc.setFillColor(...THEME_GREEN);
    doc.roundedRect(logoX, logoY, logoSize, logoSize, 2, 2, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("AF", logoX + logoSize / 2, logoY + logoSize / 2 + 1, { align: "center" });
  }

  const textX = logoX + logoSize + 7;
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...THEME_GREEN);
  doc.text(COMPANY_DETAILS.name.toUpperCase(), textX, 18);

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(90, 90, 90);
  doc.text(COMPANY_DETAILS.address, textX, 23);
  doc.text(`Tel: ${COMPANY_DETAILS.phone} | Mobile: ${COMPANY_DETAILS.mobile}`, textX, 27);
  doc.text(`Email: ${COMPANY_DETAILS.email} | Web: ${COMPANY_DETAILS.website}`, textX, 31);

  // ----- Document title (right aligned) -----
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...THEME_GREEN);
  doc.text("INVENTORY ITEMS REPORT", pageWidth - margin, 19, { align: "right" });

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${dayjs().format("YYYY-MM-DD HH:mm")}`, pageWidth - margin, 25, { align: "right" });
  doc.text(`Records: ${items.length}`, pageWidth - margin, 29, { align: "right" });

  // ----- Divider -----
  doc.setDrawColor(...THEME_GREEN);
  doc.setLineWidth(0.9);
  doc.line(margin, 38, pageWidth - margin, 38);

  // ----- Filters summary -----
  let startY = 43;
  const filterParts = [];
  if (filters.search) filterParts.push(`Search: ${filters.search}`);
  if (filters.category) filterParts.push(`Category: ${filters.category}`);
  if (filters.status) filterParts.push(`Status: ${filters.status}`);
  if (filters.dateRange) filterParts.push(`Created: ${filters.dateRange}`);

  if (includeFilters && filterParts.length > 0) {
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(100, 100, 100);
    doc.text("Filters Applied:", margin, startY);
    doc.setFont("helvetica", "normal");
    doc.text(filterParts.join("   |   "), margin + 28, startY);
    startY += 6;
  }

  // ----- Table (grouped by category: category heading row, then its items) -----
  const tableColumn = [
    "Item Name",
    "Available Qty",
    "Min Order Qty",
  ];

  // Group items by category, keeping a stable alphabetical order
  const groups = {};
  (items || []).forEach((it) => {
    const cat = it.category_name || "Uncategorized";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(it);
  });
  const categoryNames = Object.keys(groups).sort((a, b) => a.localeCompare(b));

  const tableRows = [];
  categoryNames.forEach((cat) => {
    // Full-width category heading row
    tableRows.push([{
      content: cat,
      colSpan: 3,
      styles: {
        halign: "left",
        fillColor: THEME_GREEN,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 9,
      },
    }]);
    groups[cat].forEach((it) => {
      tableRows.push([
        it.item_name || "-",
        String(it.available_qty),
        String(it.reorder_qty),
      ]);
    });
  });

  autoTable(doc, {
    head: [tableColumn],
    body: tableRows,
    startY,
    theme: "grid",
    margin: { left: margin, right: margin },
    headStyles: { fillColor: THEME_GREEN, fontSize: 9, halign: "center" },
    styles: { fontSize: 8.5, cellPadding: 2, overflow: "linebreak" },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 40, halign: "center" },
      2: { cellWidth: 40, halign: "center" },
    },
    didDrawPage: (data) => {
      // Footer on every page
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(140, 140, 140);
      doc.text(
        `Page ${doc.internal.getNumberOfPages()}`,
        pageWidth - margin,
        pageHeight - 8,
        { align: "right" }
      );
      doc.text(COMPANY_DETAILS.name, margin, pageHeight - 8);
    },
  });

  return doc;
};
